// Session token management for Mapbox API
// Persists tokens in sessionStorage to reduce billing by maintaining sessions across navigation

const SESSION_TOKEN_KEY = 'mapbox_session_token';
const SESSION_EXPIRY_KEY = 'mapbox_session_expiry';
const SESSION_TTL = 30 * 60 * 1000; // 30 minutes - Mapbox recommendation

// Check if sessionStorage is available (handles SSR and private browsing)
const isSessionStorageAvailable = () => {
  try {
    const testKey = '__test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// Get existing session token or create a new one
export function getOrCreateSessionToken() {
  if (!isSessionStorageAvailable()) {
    // Fallback to generating a new token each time if sessionStorage unavailable
    return crypto.randomUUID();
  }

  const stored = sessionStorage.getItem(SESSION_TOKEN_KEY);
  const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
  
  // Check if we have a valid non-expired token
  if (stored && expiry && Date.now() < parseInt(expiry, 10)) {
    return stored;
  }
  
  // Generate new token and store it
  const token = crypto.randomUUID();
  sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  sessionStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now() + SESSION_TTL));
  
  return token;
}

// Clear the stored session token (used when user completes a selection)
export function clearSessionToken() {
  if (!isSessionStorageAvailable()) {
    return;
  }
  
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_EXPIRY_KEY);
}

// Update the expiry time for an existing token (used to extend active sessions)
export function extendSessionToken() {
  if (!isSessionStorageAvailable()) {
    return;
  }
  
  const stored = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (stored) {
    sessionStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now() + SESSION_TTL));
  }
}